import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

/** Riel del rail Colombia (USDT→COP). */
export type RemesaCoRail = 'CO' | 'COBREB';

export interface RemesaCoRequest {
  rail: RemesaCoRail;
  montoUsd: number;
  beneficiarioId: string;
  /** COP que vio el cliente al confirmar (price-check). */
  expectedRecibeCop?: number;
  /**
   * Idempotencia (WS1.1): UUID v4 por intención de envío. Un retry de red del
   * POST con el mismo requestId devuelve la remesa ORIGINAL (no duplica). El
   * caller lo REGENERA tras un envío exitoso y al confirmar un duplicado.
   */
  requestId?: string;
  /** Override del velocity check (WS1.2): el cliente confirmó el duplicado. */
  confirmarDuplicado?: boolean;
}

/** Envío CLP→COP: paga en CLP (debita `balances`). Espejo de RemesaCoRequest. */
export interface RemesaClpRequest {
  rail: RemesaCoRail;
  montoClp: number;
  beneficiarioId: string;
  /** COP que vio el cliente al confirmar (price-check atraviesa las 2 tasas). */
  expectedRecibeCop?: number;
  requestId?: string;
  confirmarDuplicado?: boolean;
}

export interface RemesaClpResponse {
  txId: number;
  estado: string; // POR_PROCESAR
  rail: RemesaCoRail;
  montoClp: number;
  recibeCop: number;
  tasaCopClp: number;
  businessDays: number | null;
}

/** Envío VES→COP: usuario VE paga en VES (debita `wallet_cardex_ves`). Espejo de RemesaClpRequest. */
export interface RemesaVesRequest {
  rail: RemesaCoRail;
  montoVes: number;
  beneficiarioId: string;
  /** COP que vio el cliente al confirmar (price-check atraviesa VES→USD y USD→COP). */
  expectedRecibeCop?: number;
  requestId?: string;
  confirmarDuplicado?: boolean;
}

export interface RemesaVesResponse {
  txId: number;
  estado: string; // POR_PROCESAR
  rail: RemesaCoRail;
  montoVes: number;
  recibeCop: number;
  tasaCopVes: number;
  businessDays: number | null;
}

/** Rechazo especial: la tasa cambió entre el preview y el envío. */
export interface ValoresCambiaron {
  cambioValores: true;
  mensaje: string;
  recibeCop: number;
  tasaCop: number;
  businessDays: number | null;
}

/**
 * Errores ACCIONABLES del envío (WS2.2, doc 10). El componente los discrimina
 * por `tipo` para darles UX propia en vez de un mensaje genérico:
 *  - REMITENTE_DATOS_INCOMPLETOS → "Completá tu perfil" + lista + CTA
 *  - DATOS_INCOMPLETOS (beneficiario) → dialog de completado existente
 *  - POSIBLE_DUPLICADO → confirmación explícita (override confirmarDuplicado)
 *
 * Los fallos transitorios (5xx/timeout/red) ya NO son accionables: el envío se
 * reintenta automáticamente con el MISMO requestId (idempotente en backend) y
 * se resuelve en definitivo — "enviada" (200) o "no se pudo completar" (error
 * plano). El cliente nunca ve un resultado ambiguo.
 */
export interface RemesaCoErrorAccionable {
  tipo:
    | 'REMITENTE_DATOS_INCOMPLETOS'
    | 'DATOS_INCOMPLETOS'
    | 'POSIBLE_DUPLICADO';
  mensaje: string;
  /** Campos faltantes ({campo,label} del emisor; keys Vita del beneficiario). */
  faltantes?: any[];
  beneficiarioId?: string;
  variante?: string;
  /** Datos de la remesa previa (POSIBLE_DUPLICADO). */
  previa?: {
    txId: number;
    estado: string;
    enVuelo: boolean;
    /** USDT→COP. */
    montoUsd?: number;
    /** CLP→COP. */
    montoClp?: number;
    /** VES→COP. */
    montoVes?: number;
    recibeCop: number;
    creadaAt: string;
  };
}

export function esErrorAccionable(e: any): e is RemesaCoErrorAccionable {
  return !!e && typeof e === 'object' && typeof e.tipo === 'string' && typeof e.mensaje === 'string';
}

export interface RemesaCoResponse {
  txId: number;
  estado: string; // POR_PROCESAR
  rail: RemesaCoRail;
  montoUsd: number;
  recibeCop: number;
  businessDays: number | null;
}

/** Cotización cliente (datos visibles, sin internos). */
export interface CotizacionCo {
  disponible: boolean;
  rail?: RemesaCoRail;
  montoUsd?: number;
  /** Tasa COP/USD con la que el backend calcula el `recibeCop`. */
  tasaCop?: number;
  /**
   * Tasa de REFERENCIA COP/USD (mercado), SOLO para mostrar. No entra en el
   * cálculo del envío — ese lo hace el backend con `tasaCop`. Contra ella se
   * expresa el costo total en USD: `montoUsd − recibeCop / tasaReferenciaCop`.
   */
  tasaReferenciaCop?: number;
  /** Comisión total en COP (monto×tasa − recibe). */
  comisionCop?: number;
  /** COP que recibe el destinatario (entero). */
  recibeCop?: number;
  businessDays?: number | null;
}

/** Cotización cliente CLP→COP (paga en CLP). Client-safe, sin internos. */
export interface CotizacionClp {
  disponible: boolean;
  motivo?: 'INACTIVO' | 'SIN_TASA' | 'BAJO_MINIMO' | 'SIN_MARGEN';
  rail?: RemesaCoRail;
  montoClp?: number;
  recibeCop?: number;
  /** Única tasa que ve el cliente: COP por CLP. */
  tasaCopClp?: number;
  /**
   * Tasa de REFERENCIA COP por CLP (bruta, antes de comisión). Sólo alimenta el
   * desglose; `recibeCop` NO depende de ella. `null` = no hay referencia
   * confiable → mostrar sólo la efectiva.
   */
  tasaReferenciaCopClp?: number | null;
  /** Mínimo en CLP a la tasa actual. */
  minClp?: number;
  /** Máximo en CLP a la tasa actual (= 2500 USD). */
  maxClp?: number;
  /** true si está bajo el mínimo (20 USD): se cotiza pero no se envía. */
  bajoMinimo?: boolean;
  /** true si excede el máximo (2500 USD): se cotiza pero no se envía. */
  excedeMaximo?: boolean;
  /** ¿CLP→COP activo (se puede enviar)? La cotización se muestra igual. */
  activo?: boolean;
  businessDays?: number | null;
}

/** Cotización cliente VES→COP (paga en VES). Client-safe, sin internos. Espejo de CotizacionClp. */
export interface CotizacionVes {
  disponible: boolean;
  motivo?: 'INACTIVO' | 'SIN_TASA' | 'SIN_MARGEN';
  rail?: RemesaCoRail;
  montoVes?: number;
  recibeCop?: number;
  /** Única tasa que ve el cliente: COP por VES. */
  tasaCopVes?: number;
  /**
   * Tasa de REFERENCIA COP por VES (bruta, antes de comisión). Sólo alimenta el
   * desglose; `recibeCop` NO depende de ella. `null` = mostrar sólo la efectiva.
   */
  tasaReferenciaCopVes?: number | null;
  /** Mínimo en VES a la tasa actual (= 20 USD). */
  minVes?: number;
  /** Máximo en VES a la tasa actual (= 2500 USD). */
  maxVes?: number;
  /** true si está bajo el mínimo (20 USD): se cotiza pero no se envía. */
  bajoMinimo?: boolean;
  /** true si excede el máximo (2500 USD): se cotiza pero no se envía. */
  excedeMaximo?: boolean;
  /** ¿VES→COP activo (se puede enviar)? La cotización se muestra igual. */
  activo?: boolean;
  businessDays?: number | null;
}

/**
 * Cliente del rail de salida USDT→COP (endpoint `colombia/remesa-co`).
 * Crea la remesa de forma transaccional en el backend (gates + débito atómico
 * + encolado interno). La confirmación final llega async (estado POR_PROCESAR).
 */
@Injectable({ providedIn: 'root' })
export class OthersPayoutsService {
  private baseUrl = `${environment.apiNest}colombia`;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  enviarRemesaCo(body: RemesaCoRequest): Observable<RemesaCoResponse> {
    return this.postRemesaIdempotente<RemesaCoResponse>('/remesa-co', body).pipe(
      catchError((err: HttpErrorResponse) => {
        // La tasa cambió entre el preview y el envío → no se creó nada;
        // propagamos el valor nuevo para que el cliente re-confirme.
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibeCop: err.error.recibeCop,
                tasaCop: err.error.tasaCop,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaron),
          );
        }

        // 409 accionables del backend (WS2.2): NO se creó nada en ninguno.
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaCoErrorAccionable),
          );
        }

        // 5xx / timeout / red caída PERSISTENTE (ya se agotaron los reintentos
        // idempotentes de `postRemesaIdempotente`): el envío NO se completó.
        // Resultado DEFINITIVO, no ambiguo — reintentar a mano es seguro porque
        // el requestId se preserva (idempotente: no duplica).
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () =>
              'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }

        return this.handleError(err);
      }),
    );
  }

  /**
   * Envío CLP→COP (endpoint `colombia/clp/remesa`). Mismo manejo de errores
   * accionables que `enviarRemesaCo` (VALORES_CAMBIARON, REMITENTE/DATOS
   * INCOMPLETOS, POSIBLE_DUPLICADO) + reintento idempotente ante 5xx/timeout.
   */
  enviarRemesaClp(body: RemesaClpRequest): Observable<RemesaClpResponse> {
    return this.postRemesaIdempotente<RemesaClpResponse>('/clp/remesa', body).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibeCop: err.error.recibeCop,
                tasaCop: err.error.tasaCop,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaron),
          );
        }
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaCoErrorAccionable),
          );
        }
        // 5xx/timeout persistente (agotados los reintentos idempotentes):
        // resultado DEFINITIVO, reintentar a mano es seguro (mismo requestId).
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () =>
              'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }
        return this.handleError(err);
      }),
    );
  }

  /**
   * Envío VES→COP (endpoint `colombia/ves/remesa`). Mismo manejo de errores
   * accionables que `enviarRemesaCo`/`enviarRemesaClp` (VALORES_CAMBIARON,
   * REMITENTE/DATOS INCOMPLETOS, POSIBLE_DUPLICADO) + reintento idempotente
   * ante 5xx/timeout. Solo usuarios VE (el backend gatea por `tipoId='VE'`).
   */
  enviarRemesaVes(body: RemesaVesRequest): Observable<RemesaVesResponse> {
    return this.postRemesaIdempotente<RemesaVesResponse>('/ves/remesa', body).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 409 && err?.error?.codigo === 'VALORES_CAMBIARON') {
          return throwError(
            () =>
              ({
                cambioValores: true,
                mensaje: err.error.mensaje,
                recibeCop: err.error.recibeCop,
                tasaCop: err.error.tasaCop,
                businessDays: err.error.businessDays ?? null,
              } as ValoresCambiaron),
          );
        }
        const codigo = err?.status === 409 ? err?.error?.codigo : null;
        if (
          codigo === 'REMITENTE_DATOS_INCOMPLETOS' ||
          codigo === 'DATOS_INCOMPLETOS' ||
          codigo === 'POSIBLE_DUPLICADO'
        ) {
          return throwError(
            () =>
              ({
                tipo: codigo,
                mensaje: err.error.mensaje || 'Revisá los datos e intentá nuevamente.',
                faltantes: err.error.faltantes,
                beneficiarioId: err.error.beneficiarioId,
                variante: err.error.variante,
                previa: err.error.previa,
              } as RemesaCoErrorAccionable),
          );
        }
        // 5xx/timeout persistente (agotados los reintentos idempotentes):
        // resultado DEFINITIVO, reintentar a mano es seguro (mismo requestId).
        if (err?.status === 0 || (err?.status >= 500 && err?.status <= 599)) {
          return throwError(
            () =>
              'No se pudo completar el envío. Volvé a intentarlo en unos minutos.',
          );
        }
        return this.handleError(err);
      }),
    );
  }

  /** Cotización real (Modelo A) para mostrar al cliente — concuerda con el envío. */
  cotizar(rail: RemesaCoRail, montoUsd: number): Observable<CotizacionCo> {
    return this.postConRetry401<CotizacionCo>('/cotizar', {
      rail,
      montoUsd,
    }).pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Cotización cliente CLP→COP (paga en CLP). Espejo de `cotizar` para origen CLP. */
  cotizarClp(rail: RemesaCoRail, montoClp: number): Observable<CotizacionClp> {
    return this.postConRetry401<CotizacionClp>('/clp/cotizar', {
      rail,
      montoClp,
    }).pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Cotización cliente VES→COP (paga en VES). Espejo de `cotizarClp` para origen VES. */
  cotizarVes(rail: RemesaCoRail, montoVes: number): Observable<CotizacionVes> {
    return this.postConRetry401<CotizacionVes>('/ves/cotizar', {
      rail,
      montoVes,
    }).pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * POST con UN reintento automático ante 401: el token puede vencer entre el
   * preview y el click de confirmar. El 401 sale del guard (no se creó nada),
   * así que reintentar con token fresco es seguro. Si el 401 persiste, cae al
   * handleError (mensaje de sesión, no "Error del servidor: 401").
   */
  /**
   * POST del envío con reintento automático SOLO ante fallo transitorio (red
   * caída = status 0, o 5xx del servidor). Es SEGURO porque el body lleva un
   * `requestId` FIJO y el backend es idempotente (UNIQUE `uk_vtl_client_request_id`
   * + replay): si el POST original SÍ creó la remesa, el reintento devuelve la
   * ORIGINAL (200); si no la creó, la crea ahora. Nunca duplica (adiós caso
   * Edward, resuelto en definitivo en vez de mostrar un aviso ambiguo).
   *
   * Los errores DEFINITIVOS (4xx: 401, 409 accionables/VALORES_CAMBIARON, 400)
   * NO se reintentan — se propagan de inmediato al catchError del caller.
   */
  private readonly REINTENTOS_TRANSITORIOS = 2;

  private postRemesaIdempotente<T>(path: string, body: unknown): Observable<T> {
    return this.postConRetry401<T>(path, body).pipe(
      retry({
        count: this.REINTENTOS_TRANSITORIOS,
        delay: (err: HttpErrorResponse) => {
          const transitorio =
            err?.status === 0 || (err?.status >= 500 && err?.status <= 599);
          // Definitivo → cortar el retry y propagar (no tragarse un 409).
          if (!transitorio) return throwError(() => err);
          return timer(1200);
        },
      }),
    );
  }

  private postConRetry401<T>(path: string, body: unknown): Observable<T> {
    const doPost = () =>
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.post<T>(`${this.baseUrl}${path}`, body, { headers }),
        ),
      );
    return doPost().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status !== 401) return throwError(() => err);
        this.cognitoService.clearCache();
        return doPost();
      }),
    );
  }

  private async getHeaders(): Promise<HttpHeaders> {
    // idToken, NO accessToken: el backend valida con `tokenUse: 'id'`
    // (cognito.module.ts) — es el mismo token que manda ApiRestInterceptor.
    // Con accessToken el guard devuelve 401 → "Sesión expirada" en bucle.
    let session = await this.cognitoService.getSession();
    let token = session?.idToken?.jwtToken;

    if (token && this.isJwtNearExpiry(token, 60)) {
      if (typeof (this.cognitoService as any).clearCache === 'function') {
        (this.cognitoService as any).clearCache();
      }
      session = await this.cognitoService.getSession();
      token = session?.idToken?.jwtToken;
    }

    if (!token) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token.trim()}`)
      // CRÍTICO: el ErrorInterceptor global APLANA los errores no-{400,422,429}
      // a string (409 → "Conflict", body destruido) y el ApiRestInterceptor
      // convierte ese string en un 401 sintético → "sesión expirada" falso.
      // Este flujo necesita el HttpErrorResponse COMPLETO (codigo, previa,
      // faltantes) para los 409 accionables → usar el escape hatch del
      // interceptor (borra el header antes de salir al backend).
      .set('X-Skip-Error-Interceptor', 'true');
  }

  private isJwtNearExpiry(jwt: string, thresholdSec: number): boolean {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (!payload?.exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp - nowSec < thresholdSec;
    } catch {
      return false;
    }
  }

  private handleError(error: HttpErrorResponse) {
    // 401 SINTÉTICO del ApiRestInterceptor (sin body): getSession() devolvió
    // null por el race del refresh de Amplify — la petición NUNCA salió del
    // browser y la sesión NO expiró. Reintentar es seguro (no hay riesgo de
    // doble envío) y suele bastar con esperar el refresh. Mensaje honesto,
    // no "sesión expirada" + "recargá" (empuja al reintento a ciegas).
    if (error?.status === 401 && !error?.error) {
      return throwError(
        () =>
          'No pudimos validar tu sesión en este momento. Esperá unos segundos e intentá nuevamente.',
      );
    }
    // 401/403 REAL del backend (con body): la sesión sí está vencida/inválida.
    if (error?.status === 401 || error?.status === 403) {
      return throwError(
        () => 'Tu sesión expiró. Volvé a iniciar sesión e intentá de nuevo.',
      );
    }
    const msg =
      error.error?.message ||
      (typeof error.error === 'string' ? error.error : '') ||
      `Error del servidor: ${error.status}`;
    return throwError(() => msg);
  }
}
